const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, digits = 1) => `${Number(n ?? 0).toFixed(digits)}%`;

export const buildQualityTqiGaugeStyle = (tqi = 0) => {
  const pct = Math.min(100, Math.max(0, Number(tqi) || 0));
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(#8b5cf6 0 ${pct}%, rgba(139,92,246,.13) ${pct}% 100%)`,
  };
};

export const buildQualityHeroHeadline = (overview = {}) => {
  const tqi = overview.teacher_quality_index ?? 0;
  const digital = overview.digital_readiness_pct ?? 0;
  if (tqi < 40 && digital > 70) return "Quality readiness needs focused intervention";
  if (tqi < 40) return "Training coverage gaps need district-level action";
  return "Quality readiness is improving with targeted training";
};

export const buildQualityHeroNarrative = (overview = {}) => {
  const digital = overview.digital_readiness_pct ?? 0;
  const ctet = overview.ctet_coverage_pct ?? 0;
  const cwsn = overview.cwsn_coverage_pct ?? 0;
  const digitalNote =
    digital > 70
      ? "Computer training coverage is strong, but CTET and CWSN preparedness are low."
      : "Training coverage is uneven across CTET, CWSN and digital readiness.";
  return `${digitalNote} Qualification risk is concentrated in a small set of high-volume blocks.`;
};

export const buildQualityStatusChips = (overview = {}) => {
  const belowGrad = overview.below_grad_pct ?? 0;
  const digital = overview.digital_readiness_pct ?? 0;
  const chips = [];
  if (belowGrad >= 8) chips.push({ label: "High Qualification Risk", className: "red" });
  else chips.push({ label: "Qualification Watch", className: "purple" });
  chips.push({ label: `${fmtPct(digital)} Computer Trained`, className: "blue" });
  chips.push({ label: "3 Training Workstreams", className: "" });
  return chips;
};

export const buildQualityPrimaryAction = (qualificationRisk = []) => {
  const top = qualificationRisk.slice(0, 3).map((b) => b.block_name).join(", ");
  if (top) {
    return `Launch a 90-day teacher qualification sprint for CTET readiness and below-graduation remediation in ${top}.`;
  }
  return "Launch a 90-day teacher qualification sprint for CTET readiness and below-graduation remediation in the highest-risk blocks.";
};

export const buildQualityKpis = (overview = {}) => [
  {
    label: "CTET Qualified",
    value: fmt(overview.ctet_cy),
    sub: `${fmtPct(overview.ctet_coverage_pct, 2)} teacher coverage`,
    cardClass: "",
  },
  {
    label: "CWSN Trained",
    value: fmt(overview.cwsn_trained_cy),
    sub: `${fmtPct(overview.cwsn_coverage_pct, 2)} inclusive education coverage`,
    cardClass: "purple",
  },
  {
    label: "Computer Trained",
    value: fmt(overview.computer_trained_cy),
    sub: `${fmtPct(overview.digital_readiness_pct, 2)} digital-readiness coverage`,
    cardClass: "cyan",
  },
  {
    label: "Below Graduation",
    value: fmt(overview.below_grad_cy),
    sub: `${fmtPct(overview.below_grad_pct, 2)} teachers needing qualification support`,
    cardClass: "red",
  },
];

export const buildDomainCards = (overview = {}) => {
  const ctetPct = overview.ctet_coverage_pct ?? 0;
  const cwsnPct = overview.cwsn_coverage_pct ?? 0;
  const digitalPct = overview.digital_readiness_pct ?? 0;
  const teachers = overview.teachers_cy ?? 0;
  const cwsnGap = Math.max(0, teachers - (overview.cwsn_trained_cy ?? 0));

  return [
    {
      id: "qualification",
      title: "Qualification Baseline",
      body: "CTET qualified share is low while below-graduation teachers create compliance risk.",
      score: fmtPct(ctetPct),
      scoreLabel: "CTET coverage",
      width: ctetPct,
      barColor: "var(--tqt-primary)",
      badge: "Needs focus",
      badgeClass: "red",
      iconClass: "",
      metaLeft: `${fmt(overview.ctet_cy)} qualified`,
      metaRight: `${fmt(overview.below_grad_cy)} below grad`,
    },
    {
      id: "cwsn",
      title: "Inclusive Education Readiness",
      body: "CWSN training needs a structured block-wise rollout and follow-up certification.",
      score: fmtPct(cwsnPct),
      scoreLabel: "CWSN trained",
      width: cwsnPct,
      barColor: "var(--tqt-purple)",
      badge: "Low readiness",
      badgeClass: "purple",
      iconClass: "purple",
      metaLeft: `${fmt(overview.cwsn_trained_cy)} trained`,
      metaRight: cwsnGap >= 1000 ? `${Math.round(cwsnGap / 1000)}K+ gap` : `${fmt(cwsnGap)} gap`,
    },
    {
      id: "digital",
      title: "Digital Teaching Readiness",
      body: "Computer training is the strongest readiness signal and can support blended training delivery.",
      score: fmtPct(digitalPct),
      scoreLabel: "Computer trained",
      width: digitalPct,
      barColor: "var(--tqt-accent)",
      badge: digitalPct >= 70 ? "Strong base" : "Building",
      badgeClass: "",
      iconClass: "green",
      metaLeft: `${fmt(overview.computer_trained_cy)} trained`,
      metaRight: "Scale digital modules",
    },
  ];
};

export const buildCoverageRows = (overview = {}, trainingCoverage = []) => {
  const ctet = trainingCoverage.find((t) => t.name === "CTET Qualified");
  const cwsn = trainingCoverage.find((t) => t.name === "CWSN Trained");
  const computer = trainingCoverage.find((t) => t.name === "Computer Trained");
  const tqi = overview.teacher_quality_index ?? 0;

  return [
    {
      label: "CTET Qualified",
      sub: `${fmt(ctet?.trained ?? overview.ctet_cy)} teachers`,
      value: fmtPct(overview.ctet_coverage_pct),
      width: overview.ctet_coverage_pct ?? 0,
      barClass: "",
    },
    {
      label: "CWSN Trained",
      sub: `${fmt(cwsn?.trained ?? overview.cwsn_trained_cy)} teachers`,
      value: fmtPct(overview.cwsn_coverage_pct),
      width: overview.cwsn_coverage_pct ?? 0,
      barClass: "purplebar",
    },
    {
      label: "Computer Trained",
      sub: `${fmt(computer?.trained ?? overview.computer_trained_cy)} teachers`,
      value: fmtPct(overview.digital_readiness_pct),
      width: overview.digital_readiness_pct ?? 0,
      barClass: "cyanbar",
    },
    {
      label: "Teacher Quality Index",
      sub: "Composite readiness score",
      value: String(tqi),
      width: tqi,
      barClass: "greenbar",
    },
    {
      label: "Below Graduation",
      sub: `${fmt(overview.below_grad_cy)} teachers`,
      value: fmtPct(overview.below_grad_pct),
      width: overview.below_grad_pct ?? 0,
      barClass: "redbar",
    },
  ];
};

export const buildCoverageInsights = (overview = {}) => {
  const ctet = overview.ctet_coverage_pct ?? 0;
  const cwsn = overview.cwsn_coverage_pct ?? 0;
  const digital = overview.digital_readiness_pct ?? 0;
  const belowGrad = overview.below_graduation_pct ?? overview.below_grad_pct ?? 0;

  const items = [
    {
      title: "Fastest Win",
      body:
        digital >= ctet
          ? `Digital readiness at ${digital.toFixed(1)}% can support CTET mentoring where CTET is ${ctet.toFixed(1)}%.`
          : `CTET coverage at ${ctet.toFixed(1)}% is the fastest certification uplift lever.`,
      score: digital >= ctet ? digital : ctet,
    },
    {
      title: "Biggest Gap",
      body: `CWSN coverage at ${cwsn.toFixed(1)}% needs block-wise completion tracking against district average.`,
      score: 100 - cwsn,
    },
    {
      title: "Highest Risk",
      body:
        belowGrad > 0
          ? `${belowGrad.toFixed(1)}% below-graduation teachers require qualification pathway mapping.`
          : "Below-graduation share is low; maintain qualification monitoring by block.",
      score: belowGrad,
    },
  ];

  return items.sort((a, b) => b.score - a.score).map(({ title, body }) => ({ title, body }));
};

export const buildQualificationRiskRows = (qualificationRisk = []) => {
  const rows = [...qualificationRisk].slice(0, 10);
  const maxVal = Math.max(...rows.map((r) => r.below_grad_cy ?? 0), 1);
  return rows.map((row) => ({
    block: row.block_name,
    value: row.below_grad_cy ?? 0,
    width: Math.round(((row.below_grad_cy ?? 0) / maxVal) * 100),
  }));
};

export const buildTqiLeaderboard = (blockData = []) => {
  const sorted = [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.quality_index ?? 0) - (a.quality_index ?? 0))
    .slice(0, 15);
  const maxScore = Math.max(...sorted.map((b) => b.quality_index ?? 0), 1);

  return sorted.map((block, index) => {
    const score = block.quality_index ?? 0;
    let barClass = "mid";
    if (index < 3) barClass = "";
    else if (index >= 10) barClass = "low";
    return {
      rank: index + 1,
      block: block.block_name,
      score,
      display: score.toFixed(1),
      width: Math.round((score / maxScore) * 100),
      barClass,
    };
  });
};

const issueForBlock = (block, qualificationRisk = []) => {
  const risk = qualificationRisk.find((r) => r.block_name === block.block_name);
  const belowGrad = risk?.below_grad_cy ?? 0;
  const tqi = block.quality_index ?? 0;
  const cwsn = block.cwsn_pct ?? 0;

  if (belowGrad >= 500) return "Below graduation concentration";
  if (tqi < 30) return "Qualification risk + low TQI";
  if (cwsn < 15) return "CWSN readiness gap";
  if ((block.ctet_pct ?? 0) < 10) return "Training gap in large teacher base";
  return "Qualification documentation gap";
};

const actionForIssue = (issue) => {
  if (issue.includes("Below graduation")) return "Qualification pathway camp";
  if (issue.includes("low TQI")) return "Teacher-wise remediation plan";
  if (issue.includes("CWSN")) return "Inclusive education workshop";
  if (issue.includes("Training gap")) return "CTET batch planning";
  return "Document validation drive";
};

export const buildInterventionQueue = (qualificationRisk = [], blockData = []) => {
  const blockMap = Object.fromEntries(blockData.map((b) => [b.block_name, b]));
  return qualificationRisk.slice(0, 5).map((risk, index) => {
    const block = blockMap[risk.block_name] ?? {};
    const issue = issueForBlock({ ...block, block_name: risk.block_name }, qualificationRisk);
    const volume = risk.below_grad_cy ?? 0;
    const qualityIndex =
      typeof block.quality_index === "number" && Number.isFinite(block.quality_index)
        ? block.quality_index
        : null;
    const status = volume >= 700 || (qualityIndex != null && qualityIndex < 30) ? "Critical" : "High";
    return {
      priority: index + 1,
      block: risk.block_name,
      issue,
      volume,
      action: actionForIssue(issue),
      status,
      statusClass: status === "Critical" ? "red" : "",
    };
  });
};

export const buildQualityActionCards = (qualificationRisk = []) => {
  const top = qualificationRisk.slice(0, 3).map((b) => b.block_name);
  const names =
    top.length >= 3
      ? `${top[0]}, ${top[1]} and ${top[2]}`
      : top.join(", ") || "highest-risk blocks";

  return [
    {
      title: "Resolve Qualification Risk First",
      body: `Start teacher-wise qualification verification and upgrade plans in ${names}.`,
      iconClass: "red",
    },
    {
      title: "Run CWSN Training Calendar",
      body: "Create weekly inclusive education cohorts, with block education officers accountable for completion.",
      iconClass: "purple",
    },
    {
      title: "Use Digital Readiness as Accelerator",
      body: "Leverage computer-trained teachers as peer mentors for CTET prep and CWSN micro-learning modules.",
      iconClass: "green",
    },
  ];
};

export const buildReviewCadenceNote = (overview = {}) => {
  const tqi = overview.teacher_quality_index ?? 0;
  return `Track CTET, CWSN and below-graduation closure every Friday until TQI improves from ${tqi} to 45+.`;
};

export const buildTeacherQualityMarkdown = (overview = {}, qualificationRisk = []) => {
  const insights = buildCoverageInsights(overview);
  const actions = buildQualityActionCards(qualificationRisk);
  return [
    "## Insights",
    `- ${buildQualityHeroHeadline(overview)}`,
    `- ${buildQualityHeroNarrative(overview)}`,
    ...insights.map((i) => `- ${i.title}: ${i.body}`),
    "",
    "## Recommendations",
    ...actions.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    `- ${buildQualityPrimaryAction(qualificationRisk)}`,
    `- ${buildReviewCadenceNote(overview)}`,
  ].join("\n");
};
